import React from 'react';

import Image from 'next/future/image';
import Link from 'next/link';

import ApplePodcastIcon from '@/components/pages/podcast/components/ApplePodcastIcon';
import RSSIcon from '@/components/pages/podcast/components/RSSIcon';
import SpotifyIcon from '@/components/pages/podcast/components/SpotifyIcon';
import Podcast from '@/types/pages/api/podcasts/Podcast';

import SideBar from '../components/Sidebar/Sidebar';

type PodcastLayoutProps = {
    children: React.ReactChild;
    podcast: Podcast;
};

// eslint-disable-next-line import/prefer-default-export
const PodcastLayout = (props: PodcastLayoutProps) => {
    const { children, podcast } = props;

    return (
        <div className="flex flex-col sm:flex-row">
            <div className="lg:inset-y-0 lg:left-0 lg:flex sm:w-4/10 lg:items-start lg:overflow-y-auto sm:border-r">
                <div className="divide-y ">
                    <ul role="list" className="leading-7 pt-8 flex justify-center gap-10 text-base font-medium">
                        {[
                            ['Spotify', SpotifyIcon, podcast.spotifyIdentifier || ''],
                            ['Apple', ApplePodcastIcon, podcast.itunesIdentifier || ''],
                            ['RSS', RSSIcon, podcast.feedUrl || ''],
                        ].map(([label, Icon, url]) => (
                            <li key={label as string} className="flex">
                                <a
                                    href={url as string}
                                    target="_blank"
                                    className="group flex items-center"
                                    aria-label={label as string}
                                    rel="noreferrer"
                                >
                                    <span className="flex whitespace-nowrap">
                                        {/* @ts-ignore */}
                                        <Icon className="h-8 w-8 fill-slate-400 group-hover:fill-slate-600" />
                                        <span className="hidden lg:ml-2 lg:block">
                                            <>{label}</>
                                        </span>
                                    </span>
                                </a>
                            </li>
                        ))}
                    </ul>

                    <div className="mt-8 pt-8 z-10 mx-auto pb-4 md:max-w-2xl lg:min-h-full lg:flex-auto">
                        <div className="px-4 sm:px-6 md:px-4 lg:px-8 xl:px-12">
                            <Link
                                href="/"
                                className="relative mx-auto block w-48 overflow-hidden bg-slate-200 shadow-xl shadow-slate-200 sm:w-64 lg:w-auto"
                                aria-label={podcast.title}
                            >
                                <>
                                    <div className="block lg:hidden">
                                        <Image
                                            className="w-full"
                                            src={podcast.imageFile}
                                            alt=""
                                            width={128}
                                            height={128}
                                            sizes="(min-width: 1024px) 20rem, (min-width: 640px) 16rem, 12rem"
                                            priority
                                        />
                                    </div>

                                    <div className="hidden lg:block">
                                        <Image
                                            className="w-full"
                                            src={podcast.imageFile}
                                            alt=""
                                            width={350}
                                            height={350}
                                            sizes="(min-width: 1024px) 20rem, (min-width: 640px) 16rem, 12rem"
                                            priority
                                        />
                                    </div>
                                </>
                            </Link>
                            <div className="mt-10 text-center lg:mt-12 lg:text-left">
                                <p className="text-xl font-bold text-slate-900">
                                    <Link href="/">{podcast.title}</Link>
                                </p>
                                <p className="mt-3 text-lg font-medium leading-8 text-slate-700">{podcast.subtitle}</p>
                            </div>
                        </div>
                        <section className="mt-10 lg:mt-12 w-full">
                            <SideBar podcast={podcast} />
                        </section>
                    </div>
                </div>
            </div>
            <div className="border-t lg:relative lg:mb-28 sm:border-t-0 sm:w-full ">
                <div className="relative">
                    <>{children}</>
                </div>
            </div>
        </div>
    );
};

export default PodcastLayout;
